import React, { useState } from 'react';
import axios from 'axios';

interface TOTPVerificationProps {
  email: string;
  idToken: string;
  onVerify: () => void;
  onCancel: () => void;
}

const TOTPVerification: React.FC<TOTPVerificationProps> = ({
  email,
  idToken,
  onVerify,
  onCancel
}) => {
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isBackupMode, setIsBackupMode] = useState<boolean>(false);

  const handleVerify = async (): Promise<void> => {
    if (!isBackupMode && code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    if (isBackupMode && code.length < 8) {
      setError('Please enter a valid backup code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/pool/api/totp/verify', {
        email,
        token: code,
        isBackupCode: isBackupMode
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (response.data.success) {
        onVerify();
      } else {
        setError(response.data.message || (isBackupMode ? 'Invalid backup code' : 'Invalid TOTP code'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 p-6 md:p-8 m-0 md:m-4 rounded-3xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-300">
      <h2 className="text-2xl md:text-3xl font-black text-center text-[#0c2a4a] dark:text-white mb-6 tracking-tight">
        {isBackupMode ? 'Backup Entry' : 'Confirm Identity'}
      </h2>

      <p className="text-slate-500 dark:text-slate-400 text-center mb-2 text-xs md:text-sm font-bold uppercase tracking-widest">
        {isBackupMode
          ? 'Enter your backup code'
          : <span>Verify access for <br /><span className="text-[#0c2a4a] dark:text-blue-400 font-black">{email}</span></span>}
      </p>
      
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 text-center">
        {isBackupMode 
          ? 'Use backup code from your authenticator app' 
          : 'Enter code from your authenticator app'}
      </p>

      <div className="mb-8">
        <input
          type="text"
          value={code}
          onChange={(e) => {
            const val = e.target.value;
            if (isBackupMode) {
              setCode(val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12));
            } else {
              setCode(val.replace(/\D/g, '').slice(0, 6));
            }
          }}
          className={`w-full py-4 text-center ${isBackupMode ? 'text-xl tracking-[0.2em]' : 'text-3xl font-black tracking-[0.5em]'} rounded-2xl bg-slate-50 dark:bg-slate-950/50 text-[#0c2a4a] dark:text-white border-2 border-slate-200 dark:border-slate-800 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300 dark:placeholder:text-slate-700`}
          placeholder={isBackupMode ? "BACKUPCODE" : "000000"}
          autoFocus
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 text-red-600 dark:text-red-400 rounded-2xl text-sm font-medium animate-in fade-in slide-in-from-top-2">
          {error}
        </div>
      )}

      <div className="flex gap-4 mb-6">
        <button
          onClick={onCancel}
          className="flex-1 px-6 py-3 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-medium text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700"
        >
          Cancel
        </button>
        <button
          onClick={handleVerify}
          disabled={loading || (isBackupMode ? code.length < 8 : code.length !== 6)}
          className="flex-1 px-6 py-3 bg-[#0A3A6B] text-white rounded-2xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/10 hover:bg-[#0A4A8B] active:scale-[0.98] flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : 'Confirm'}
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={() => {
            setIsBackupMode(!isBackupMode);
            setCode('');
            setError('');
          }}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium transition-colors"
        >
          {isBackupMode ? 'Use Authenticator App Code' : 'Use Backup Code Instead'}
        </button>
      </div>
    </div>
  );
};

export default TOTPVerification;